import { FC, useCallback, useMemo } from 'react';
import Page, { PageProps } from '@/components/page';
import { QueryKey } from '@/lib/query-client';
import Button from '@/components/button';
import PageHeader from '@/components/page/header';
import Tooltip from '@/components/tooltip';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { GridCard } from '@/components/card-grid/helpers';
import { useQuery } from 'react-query';
import { createAgent, getAllAgents } from '@/lib/services/agent.service';
import AgentLink from '@/components/agent-link';
import { useNavigate } from '@tanstack/react-router';
import useDrawer from '@/hooks/use-drawer.hook';
import useToast from '@/hooks/use-toast.hook';
import { AgentModel } from '@/lib/models/agent.model';
import CardGrid from '@/components/card-grid';
import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';

const useStyles = createUseStyles((theme: Theme) => ({
  portalBadge: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

const AgentsPage: FC<PageProps> = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { openDrawer, closeDrawer } = useDrawer();
  const { errorToast } = useToast();
  const { canChangeAgents, getTooltip } = useFeature();
  const query = useQuery([QueryKey.AgentsList], async () => getAllAgents());

  const { data: agents, refetch } = query;

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleView = useCallback(
    (id: string) => {
      navigate({ to: '/wais/assistants/$agentId', params: { agentId: id } });
    },
    [navigate]
  );

  const handleAdd = () => {
    openDrawer('new-with-name', {
      title: 'Create New Assistant',
      content: 'Please enter a name for the assistant',
      persistToUrl: false,
      onSubmit: ({ name }: { name: string }) => {
        createAgent(new AgentModel({ name })).then(({ created, model, error }) => {
          if (created) {
            closeDrawer();
            navigate({ to: '/wais/assistants/$agentId', params: { agentId: model!.id } });
            return;
          }

          errorToast(error || 'The assistant was not created');
        });
      },
    });
  };

  const agentCards: GridCard[] = useMemo(() => {
    return (agents || []).map((agent) => {
      const { id, name, description } = agent;

      return {
        id: id,
        title: name,
        detail: description,
        children: (
          <Stack
            direction="row"
            flexGrow={1}
            width="100%"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            <Stack
              direction="row"
              gap={1}
              width="100%"
              p={1}
              height={60}
              className={styles.portalBadge}
            >
              <AgentLink agent={agent.meta} size="small" variant="text" />
            </Stack>
          </Stack>
        ),
        onClick: () => handleView(id),
      };
    });
  }, [agents, handleView, styles]);

  return (
    <Page title="Assistants" query={query}>
      <PageHeader>
        <PageBlurb>
          Create new assistants, and view and change settings for existing assistants.
        </PageBlurb>

        <PageTools>
          <Tooltip title={getTooltip('add-agent')} disabled={canChangeAgents}>
            <Button
              label="Add Assistant"
              icon="plus"
              onClick={() => handleAdd()}
              disabled={!canChangeAgents}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-agent" can={canChangeAgents} />

      <CardGrid
        title="Assistants"
        cards={agentCards}
        showAdd={false}
        showEmpty
        onRefresh={handleRefresh}
        size="large"
      />
    </Page>
  );
};

export default AgentsPage;
